import CountUp from 'react-countup'
import { Option as O, Array as A, pipe } from 'effect'
import { TrophyStarIcon } from '@repo/ui'
import { useClientSideTranslation } from 'src/services/i18n'
import type { MarbleGame } from 'src/types/domain/Game'
import AngleDoubleLeft from 'src/app/assets/icons/ico-angle-double-left.svg'
import AngleDoubleRight from 'src/app/assets/icons/ico-angle-double-right.svg'
import Ducks from 'src/app/assets/icons/ico-ducks.svg'
import type { Bet } from 'src/types/domain/Bet'
import { getCurrencySymbol } from 'src/utils/getCurrencySymbol'
import { isRaceSettledEvent } from 'src/types/domain/RaceEvent'
import { displayMoney, getSmartDecimals } from 'src/utils/displayAmount'
import { useGetCurrentLanguage } from 'src/store/game'
import { getTranslatedGameTitle } from 'src/services/i18n/utils/gameTranslations'
import { transformPresets, useViewportScale } from 'src/hooks/useViewportScale'
import { RaceBannerResults } from './components/RaceBannerResults'

type Props = Readonly<{
  game: MarbleGame
  currentBet?: Bet
  showBanner: boolean
  exitBanner: boolean
  latestEventType?: string
}>

export function ShowRaceBanner({
  game,
  showBanner,
  exitBanner,
  latestEventType,
  currentBet
}: Props) {
  const { t } = useClientSideTranslation(useGetCurrentLanguage())
  const currentRace = game.races[0]
  const raceSettledEvent = pipe(
    currentRace.raceEvents,
    A.findFirst(isRaceSettledEvent),
    O.getOrUndefined
  )
  const scaleStyle = useViewportScale({
    transform: transformPresets.overlayBanners,
    transformOrigin: 'left center'
  })

  // For crash games (crash), results are shown in the bet panel — no banner needed
  const isCrashGame = game.track.gameType === 'crash'
  if (isCrashGame) {
    return null
  }

  // For irl_alt, show win/lose banner
  const isIrlAltGame =
    game.track.gameType === 'irl_alt' ||
    game.track.gameType === 'duck_river' ||
    game.track.gameType === 'snow_run' ||
    game.track.gameType === 'social_betting'
  const playerWon = currentBet?.status === 'won'
  const playerLost = currentBet?.status === 'lost'

  // For irl_alt, render simplified banner for win or lose
  if (isIrlAltGame && raceSettledEvent && currentBet && (playerWon || playerLost)) {
    const borderState = playerWon ? 'irl-alt-won' : 'irl-alt-lost'
    return (
      <>
        <div
          className={`race-borders ${borderState} ${showBanner ? 'active' : ''} ${
            exitBanner ? 'exit' : ''
          }`}
        >
          <div className="border-a" />
          <div className="border-b" />
          <div className="border-c" />
          <div className="border-d" />
        </div>
        <div
          className="broadcast-overlay__stats broadcast-overlay__stats--centered"
          style={scaleStyle}
        >
          <div className={`entries ${showBanner ? 'active' : ''} ${exitBanner ? 'exit' : ''}`}>
            {playerWon ? (
              <div className="banner-won banner-won--irl">
                <AngleDoubleRight className="arrow arrow-left" />
                <div className="banner-won__inner">
                  <span className="won-text">{t('You Won')}</span>
                  <span className="won-amount">
                    {getCurrencySymbol(currentBet.currency)}
                    <CountUp
                      decimals={getSmartDecimals(currentBet.potentialPayout)}
                      duration={2}
                      end={Number(displayMoney(currentBet.potentialPayout))}
                      separator=","
                    />
                  </span>
                </div>
                <AngleDoubleLeft className="arrow arrow-right" />
              </div>
            ) : (
              <div className="banner-lost banner-lost--irl">
                <AngleDoubleRight className="arrow arrow-left" />
                <div className="banner-lost__inner">
                  <span className="lost-text">{t('Better luck next time')}</span>
                </div>
                <AngleDoubleLeft className="arrow arrow-right" />
              </div>
            )}
          </div>
        </div>
      </>
    )
  }

  // For irl_alt with no bet, don't show banner
  if (isIrlAltGame) {
    return null
  }

  return (
    <>
      <div
        className={`race-borders ${latestEventType} ${showBanner ? 'active' : ''} ${
          exitBanner ? 'exit' : ''
        }`}
      >
        <div className="border-a" />
        <div className="border-b" />
        <div className="border-c" />
        <div className="border-d" />
      </div>
      <div className="broadcast-overlay__stats" style={scaleStyle}>
        <div className={`entries ${showBanner ? 'active' : ''} ${exitBanner ? 'exit' : ''}`}>
          <div className={`entry-box ${latestEventType}`}>
            <div className="entry-box__inner">
              <div
                className="entry-box__background-image"
                style={{ backgroundImage: `url(${game.track.background})` }}
              />
              <div className="entry-box__inner-header">
                <div className="entry-box__inner-header-icon">
                  <TrophyStarIcon />

                  <span className="circle-one" />
                  <span className="circle-two" />
                  <span className="circle-three" />
                  <span className="circle-four" />
                </div>
                <div className="entry-box__inner-header-title">
                  {game.track.gameType === 'ducks' ? (
                    <Ducks />
                  ) : (
                    getTranslatedGameTitle(game.track, t)
                  )}
                </div>
                <div className="entry-box__inner-header-subtitle">{t('Game Results')}</div>
              </div>
              {raceSettledEvent ? (
                <>
                  {currentBet && currentBet.status === 'won' ? (
                    <div className="banner-won">
                      <AngleDoubleRight className="arrow arrow-left" />
                      <div className="banner-won__inner">
                        <span className="won-text">{t('You Won')}</span>
                        <span className="won-amount">
                          {getCurrencySymbol(currentBet.currency)}
                          <CountUp
                            decimals={getSmartDecimals(currentBet.potentialPayout)}
                            duration={2}
                            end={Number(displayMoney(currentBet.potentialPayout))}
                            separator=","
                          />
                        </span>
                      </div>
                      <AngleDoubleLeft className="arrow arrow-right" />
                    </div>
                  ) : null}

                  <div className={`banner-results banner-results__${game.track.gameType}`}>
                    <RaceBannerResults
                      marbles={game.marbles}
                      race={currentRace}
                      winnerOnly={game.resultsToSubmit === 1}
                    />
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
